import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { getMembres } from '../services/api';
import { notificationService } from '../utils/notifications';
import './ListeMontantsPrint.css';

const ListeMembresRecoltesPrint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    association: '',
    commune: ''
  });

  // Récupérer les paramètres depuis l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setFilters({
      association: params.get('association') || '',
      commune: params.get('commune') || ''
    });
  }, [location]);

  useEffect(() => {
    const loadMembres = async () => {
      try {
        const response = await getMembres();
        setMembres(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des membres:', error);
        notificationService.error('Erreur', 'Impossible de charger les membres');
      } finally {
        setLoading(false);
      }
    };

    loadMembres();
  }, []);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Membres_Recoltes_${filters.association.replace(/\s+/g, '_')}_${filters.commune.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['.print-header', '.print-table'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
      notificationService.success('PDF généré', 'Le fichier PDF a été téléchargé avec succès.');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      notificationService.error('Erreur', 'Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handleClose = () => {
    window.close();
    if (!document.hidden) {
      navigate('/admin');
    }
  };

  // Filtrer les membres par association et commune
  const membresFiltres = membres.filter(membre => {
    if (filters.association && membre.association !== filters.association) return false;
    if (filters.commune && filters.commune !== 'toutes' && membre.commune !== filters.commune) return false;
    return true;
  }).sort((a, b) => {
    // Trier par nom
    const nomA = `${a.nom} ${a.postnom || ''} ${a.prenom}`.trim();
    const nomB = `${b.nom} ${b.postnom || ''} ${b.prenom}`.trim();
    return nomA.localeCompare(nomB);
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des membres...</p>
      </div>
    );
  }

  return (
    <div className="liste-montants-print-page" style={{ display: 'block', visibility: 'visible' }}>
      <div className="print-toolbar" style={{ display: 'flex' }}>
        <div style={{ flex: 1 }}>
          <button className="btn btn-secondary" onClick={handleClose}>
            ✕ Fermer
          </button>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Imprimer
          </button>
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            📥 Télécharger PDF
          </button>
        </div>
      </div>

      <div ref={printRef} className="print-content" style={{ display: 'block', visibility: 'visible', background: 'white' }}>
        <div className="print-header">
          <h1>Liste des Membres Récoltés</h1>
          <div style={{ marginTop: '15px' }}>
            <p className="print-date">
              <strong>Association:</strong> {filters.association || 'Toutes'}
            </p>
            <p className="print-date" style={{ marginTop: '5px' }}>
              <strong>Commune:</strong> {filters.commune === 'toutes' ? 'Toutes les communes' : (filters.commune || 'Toutes')}
            </p>
            <p className="print-date" style={{ marginTop: '5px' }}>
              <strong>Date d'édition:</strong> {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="print-table-container">
          <table className="print-table">
            <thead>
              <tr>
                <th>Matricule</th>
                <th>Nom</th>
                <th>Postnom</th>
                <th>Prénom</th>
                <th>Date de naissance</th>
                <th>Téléphone</th>
                <th>Quartier</th>
                <th>Profession</th>
                <th>Date d'adhésion</th>
              </tr>
            </thead>
            <tbody>
              {membresFiltres.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                    Aucun membre trouvé avec les critères sélectionnés
                  </td>
                </tr>
              ) : (
                membresFiltres.map(membre => (
                  <tr key={membre.id}>
                    <td>{membre.numero_matricule || 'N/A'}</td>
                    <td>{membre.nom || '-'}</td>
                    <td>{membre.postnom || '-'}</td>
                    <td>{membre.prenom || '-'}</td>
                    <td>{membre.date_naissance ? new Date(membre.date_naissance).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>{membre.telephone || '-'}</td>
                    <td>{membre.quartier || '-'}</td>
                    <td>{membre.profession || '-'}</td>
                    <td>{membre.date_adhesion ? new Date(membre.date_adhesion).toLocaleDateString('fr-FR') : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="print-footer">
          <p>Total membres: {membresFiltres.length}</p>
        </div>
      </div>
    </div>
  );
};

export default ListeMembresRecoltesPrint;

